import { skipToken, useApolloClient } from '@apollo/client/react'
import { type PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import Text from 'src/components/ui/Text'
import { useCheckEmailVerified } from 'src/features/User/ForceVerifyEmail/useForceVerifyEmail'
import {
  type Message,
  type RefType,
} from 'src/services/apollo/generated/graphql'
import { useInfiniteListMessages } from 'src/services/apollo/message'
import { useSendCommentWithTurnstile } from 'src/services/helpers/comment'
import { getNodesFromConnection } from 'src/utils/graphql'
import { track } from 'src/utils/tracker'
import { getErrorData } from '../../utils/errorUtil'
import { UIDialog } from '../ui/Modal'
import {
  type CanDeleteMessage,
  CommentContext,
  createComposerStateAtom,
  DEFAULT_LOAD_SIZE,
  type DeleteMessage,
  type SendMessage,
} from './context'
import { deleteMessage, sendMessage } from './mutations'

/**
 * Comment context provider
 *
 * Necessary to make most of the comment components work.
 */
export function CommentProvider(
  props: PropsWithChildren & {
    topicId?: string
    refType?: RefType
    /** Badges comments from this user as "Creator". */
    creatorId?: string
    /** From the topic fragment (e.g., `artwork.commentCount`). */
    totalCount?: number
    /** Opt in to Apollo re-renders on networkStatus changes so "Load more"
     * shows a loading state during fetchMore. */
    notifyOnNetworkStatusChange?: boolean
    /** How many comments to render on the first load */
    initLoadSize?: number
    onLoadMore?: () => void
    canDeleteMessage?: CanDeleteMessage
    scene?: string
  },
) {
  /*
   * Comment data-related
   */

  const { data, loading, fetchMore, loadingMore } = useInfiniteListMessages(
    props.topicId
      ? {
          topicId: props.topicId,
          last: props.initLoadSize ?? 10,
        }
      : skipToken,
    props.notifyOnNetworkStatusChange
      ? { notifyOnNetworkStatusChange: true }
      : undefined,
  )

  // Read the *active* Apollo client (the one provided by the nearest
  // `<ApolloProvider>` ancestor) so the comment send/delete mutations
  // run against the same cache that `useInfiniteListMessages` above is
  // subscribed to. Without this, on routes that mount a route-scoped
  // Apollo client (artwork detail, model detail — see e.g.
  // `clientRoutes/_app.artwork.$idSlug.full-ssr.tsx`) the imperative
  // `executeSendMessageV2` / `executeDeleteMessage` default to
  // `baseApolloClient.mutate(...)` and the `update(cache, …)` callback
  // in `services/apollo/message.ts` writes the new edge into
  // `baseApolloClient.cache`, leaving the route-scoped comment list
  // stuck showing pre-mutation state until full page reload.
  const apolloClient = useApolloClient()

  const havingMore = data?.pageInfo?.hasPreviousPage ?? true
  // Used by the Footer to hide "reached the end" when the initial load
  // contained everything.
  const everHadMoreRef = useRef(false)
  if (data?.pageInfo?.hasPreviousPage === true) {
    everHadMoreRef.current = true
  }
  const messages = useMemo(
    () =>
      getNodesFromConnection(data)
        .filter(m => m != null)
        .reverse() ?? [],
    [data],
  )
  const messageCache = useMemo(() => new Map<string, Message>(), [])
  const navigate = useNavigate()

  useEffect(() => {
    for (const message of messages) {
      if (message) {
        messageCache.set(message.id, message)
      }
    }
  }, [messages, messageCache])

  function loadMore(newPageSize: number = DEFAULT_LOAD_SIZE) {
    if (!havingMore) return
    fetchMore({
      variables: {
        before: data?.pageInfo.startCursor,
        last: newPageSize,
      },
    })
    props.onLoadMore?.()
  }

  const doDeleteMessage: DeleteMessage = async (
    messageId,
    topicRefType = props.refType,
  ) => {
    if (topicRefType) {
      await deleteMessage(messageId, topicRefType, apolloClient)
    }
  }

  /*
   * Composer-related
   */

  const composerStateAtom = useMemo(createComposerStateAtom, [])
  const checkEmailVerified = useCheckEmailVerified()
  const { getTurnstileToken, TurnstileWidget, turnstileReady, resetTurnstile } =
    useSendCommentWithTurnstile()

  const doSendMessage: SendMessage = async (content, replyTo) => {
    if (!(await checkEmailVerified())) return
    if (props.topicId) {
      try {
        if (!props.refType) {
          throw new Error('Unknown topic type')
        }

        const headers: Record<string, string> = {}
        const token = getTurnstileToken()
        if (token) {
          headers['X-Turnstile-Token'] = token
        }

        await sendMessage(
          props.topicId,
          props.refType,
          content,
          replyTo,
          headers,
          apolloClient,
        )
        resetTurnstile()
        track('send_message', {
          type: props.scene,
          isReply: Boolean(replyTo),
        })
      } catch (err) {
        const errCode = getErrorData(err)?.code
        if (
          errCode === 40300012 // Not verified email
        ) {
          await UIDialog.alert({
            heading: (
              <Text i18nKey="comment:actions.send-comment.verify-email.title" />
            ),
            content: (
              <Text i18nKey="comment:actions.send-comment.verify-email.content" />
            ),
            confirmProps: {
              onPress: () => {
                navigate('/profile/edit/accounts')
              },
              children: <Text i18nKey="comment:actions.verify-now" />,
            },
          })
        } else if (
          errCode === 40000013 // User blocked
        ) {
          toast.error(
            <>
              <Text i18nKey="common:block.denied-message" />
            </>,
          )
        } else {
          const msg = (err as Error).message ?? String(err)
          toast.error(
            <>
              <Text i18nKey="comment:actions.send-comment.messages.failed" />
              {` (${msg})`}
            </>,
          )
        }
        return Promise.reject(err)
      }
    }
  }

  return (
    <CommentContext.Provider
      value={{
        refType: props.refType,
        creatorId: props.creatorId,
        totalCount: props.totalCount,
        messages,
        messageCache,
        havingMore,
        everHadMore: everHadMoreRef.current,
        loading: loading || loadingMore,
        loadMore,
        canDeleteMessage: props.canDeleteMessage,
        deleteMessage: doDeleteMessage,
        composerStateAtom,
        sendMessage: doSendMessage,
        TurnstileWidget,
        turnstileReady,
      }}
    >
      {props.children}
    </CommentContext.Provider>
  )
}
